// Solution Studio API client — CRUD + async-job pattern (mirrors agentStudioClient).
//
// All calls go to `${API_BASE}/solution-studio/...` and carry the Azure AD Bearer token from
// getApiToken(). Writes to a project use optimistic concurrency: pass the `expectedVersion` you read;
// a 409 { reason } means the row changed under you (stale-version) or a generation job holds the
// lock — the UI reconciles per §4.5.

const API_BASE = process.env.REACT_APP_SOLUTION_STUDIO_API_BASE_URL || '';

export function isSolutionStudioBackendConfigured() {
  return Boolean(API_BASE);
}

async function authHeaders(tokenProvider) {
  const headers = { 'Content-Type': 'application/json' };
  if (tokenProvider) {
    try {
      const token = await tokenProvider();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (err) {
      console.warn('solnstudio: token acquisition failed', err);
    }
  }
  return headers;
}

async function parseOrThrow(res, fallbackPrefix) {
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { code: 'INVALID_RESPONSE', message: text };
  }
  if (!res.ok) {
    const err = new Error(data?.message || `${fallbackPrefix} failed (HTTP ${res.status})`);
    err.code = data?.code;
    err.reason = data?.reason; // 'stale-version' | 'locked' on a 409
    err.status = res.status;
    throw err;
  }
  return data;
}

function requireBase() {
  if (!API_BASE) {
    throw new Error('Solution Studio API URL is not configured. Set REACT_APP_SOLUTION_STUDIO_API_BASE_URL.');
  }
}

async function call(method, path, { body, tokenProvider } = {}) {
  requireBase();
  const headers = await authHeaders(tokenProvider);
  const res = await fetch(`${API_BASE}/solution-studio/${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return parseOrThrow(res, `${method} ${path}`);
}

// ---- Projects ----
export async function listProjects(tokenProvider) {
  const { projects } = await call('GET', 'projects', { tokenProvider });
  return projects || [];
}

export async function createProject(body, tokenProvider) {
  const { project } = await call('POST', 'projects', { body, tokenProvider });
  return project;
}

export async function getProject(id, tokenProvider) {
  const { project } = await call('GET', `projects/${encodeURIComponent(id)}`, { tokenProvider });
  return project;
}

/** Guarded metadata write. `fields` is a flat object of project columns to set. */
export async function updateProject(id, expectedVersion, fields, tokenProvider) {
  const { project } = await call('PATCH', `projects/${encodeURIComponent(id)}`, {
    body: { expectedVersion, fields },
    tokenProvider,
  });
  return project;
}

/** Hard-delete a project + purge all its backend storage. Guarded by expectedVersion (409 on conflict). */
export async function deleteProject(id, expectedVersion, tokenProvider) {
  const { project } = await call('DELETE', `projects/${encodeURIComponent(id)}`, {
    body: { expectedVersion },
    tokenProvider,
  });
  return project;
}

// ---- Source documents ----
/** Register an uploaded source doc (text already extracted client-side). Guarded by expectedVersion. */
export async function uploadSource(id, expectedVersion, doc, tokenProvider) {
  const { project, doc: saved } = await call('POST', `projects/${encodeURIComponent(id)}/sources`, {
    body: { expectedVersion, ...doc },
    tokenProvider,
  });
  return { project, doc: saved };
}

/**
 * Suggest a semantic role for a doc (cheap Bedrock Haiku call; no project write). Best-effort —
 * resolves to { role, confidence, rationale } and the backend defaults to 'other' on any failure.
 * `excerpt` should be the first few thousand chars of the extracted text.
 */
export async function classifySource(id, { filename, kind, excerpt }, tokenProvider) {
  return call('POST', `projects/${encodeURIComponent(id)}/sources/classify`, {
    body: { filename, kind, excerpt },
    tokenProvider,
  });
}

/** Update a source doc's semantic role. Guarded by expectedVersion. */
export async function updateSourceRole(id, expectedVersion, docId, role, tokenProvider) {
  const { project } = await call('PATCH', `projects/${encodeURIComponent(id)}/sources/${encodeURIComponent(docId)}`, {
    body: { expectedVersion, role },
    tokenProvider,
  });
  return project;
}

/** Remove a source doc. Guarded by expectedVersion (sent in the body). */
export async function deleteSource(id, expectedVersion, docId, tokenProvider) {
  const { project } = await call('DELETE', `projects/${encodeURIComponent(id)}/sources/${encodeURIComponent(docId)}`, {
    body: { expectedVersion },
    tokenProvider,
  });
  return project;
}

// ---- Design Model ----
export async function getDesignModel(id, tokenProvider) {
  return call('GET', `projects/${encodeURIComponent(id)}/design-model`, { tokenProvider });
}

/**
 * Save an edited Flow DSL into the Design Model (explicit Save — edits are NOT autosaved; optimistic
 * concurrency, 409 reason on conflict). Pass `{ layoutOnly: true }` for the one-time auto-arrange save
 * — the backend then persists the positions WITHOUT marking the flow human-edited, so a later Refine
 * can still regenerate it.
 */
export async function saveFlowDsl(id, flowId, expectedVersion, dsl, tokenProvider, { layoutOnly = false } = {}) {
  return call('PATCH', `projects/${encodeURIComponent(id)}/flows/${encodeURIComponent(flowId)}`, {
    body: { expectedVersion, dsl, layoutOnly },
    tokenProvider,
  });
}

/**
 * Answer/resolve (or reopen) an open question. The backend records a paired, human-locked decision so
 * the next generation pass treats it as settled and never re-asks it. Guarded by expectedVersion
 * (409 reason on conflict). Pass `status: 'open'` to reopen a previously resolved question.
 */
export async function answerOpenQuestion(id, oqId, { expectedVersion, answer, status }, tokenProvider) {
  return call('PATCH', `projects/${encodeURIComponent(id)}/open-questions/${encodeURIComponent(oqId)}`, {
    body: { expectedVersion, answer, status },
    tokenProvider,
  });
}

/**
 * Apply structured edits back into the Design Model (from a marked-up DDD round-trip). `ops` are
 * JSON-Patch-shaped: { op:'replace', path:'/<collection>/<id>/<field>', value } or
 * { op:'remove', path:'/<collection>/<id>' }. Guarded by expectedVersion (409 reason on conflict).
 */
export async function applyDesignEdits(id, expectedVersion, ops, tokenProvider) {
  return call('POST', `projects/${encodeURIComponent(id)}/design-model/apply-edits`, {
    body: { expectedVersion, ops },
    tokenProvider,
  });
}

// ---- Generation (async job) ----
// `stage` (design kind only) selects which analyze slices run: 'design' = light slices (NLU / prompts
// / integrations); 'flows' = the Opus flows slice (the explicit Diagrams-tab "Generate flows" action).
export async function submitGeneration(
  id,
  { kind = 'design', mode = 'deep', stage = 'design', targetFlow, feedback, planOnly, usePlan, overrideApproval } = {},
  tokenProvider
) {
  // targetFlow/feedback (flows stage only) drive the targeted single-flow regenerate; omitted on a full run.
  // planOnly/usePlan (flows stage only) drive the plan-approval gate: planOnly runs just the architect and
  // persists the FLOW_PLAN for review; usePlan builds from that approved plan (skipping the architect).
  // overrideApproval (build kind only) bypasses the P2.5 design-approval soft gate after a UI confirm.
  const body = { kind, mode, stage };
  if (targetFlow) body.targetFlow = targetFlow;
  if (feedback) body.feedback = feedback;
  if (planOnly) body.planOnly = true;
  if (usePlan) body.usePlan = true;
  if (overrideApproval) body.overrideApproval = true;
  return call('POST', `projects/${encodeURIComponent(id)}/generate`, { body, tokenProvider });
}

// §P2.5 design sign-off: records the approved designModelVersion (+ status DESIGN_APPROVED). Build
// soft-gates on the approval being CURRENT (a later refine bumps the version → approval goes stale).
export async function approveDesign(id, expectedVersion, tokenProvider) {
  return call('POST', `projects/${encodeURIComponent(id)}/approve-design`, {
    body: { expectedVersion },
    tokenProvider,
  });
}

export async function pollJob(jobId, tokenProvider) {
  if (!jobId) throw new Error('jobId is required.');
  const { job } = await call('GET', `jobs/${encodeURIComponent(jobId)}`, { tokenProvider });
  return job;
}

// ---- Artifacts ----
/**
 * Upload a client-generated deliverable (.xlsx) as a versioned artifact. `doc` =
 * { id, type, filename, contentBase64, version }. Guarded by expectedVersion (409 reason on conflict).
 * Upserts by id (one current artifact per deliverable type; revision increments).
 */
export async function uploadArtifact(id, expectedVersion, doc, tokenProvider) {
  const { project, artifact } = await call('POST', `projects/${encodeURIComponent(id)}/artifacts`, {
    body: { expectedVersion, ...doc },
    tokenProvider,
  });
  return { project, artifact };
}

export async function getArtifactUrl(id, artifactId, tokenProvider) {
  return call('GET', `projects/${encodeURIComponent(id)}/artifacts/${encodeURIComponent(artifactId)}`, {
    tokenProvider,
  });
}

/**
 * Overwrite the FLOW_PLAN artifact with an engineer-edited flow inventory (rename / re-type / remove
 * planned flows before building). `plan` is the edited entry array; a follow-up usePlan build reads it.
 * Guarded by expectedVersion (409 reason on conflict).
 */
export async function saveFlowPlan(id, expectedVersion, plan, tokenProvider) {
  return call('POST', `projects/${encodeURIComponent(id)}/flow-plan`, {
    body: { expectedVersion, plan },
    tokenProvider,
  });
}

/**
 * Record imported Connect resource IDs into the project's per-instance import ledger
 * (project.connectResources). `payload` = { instanceId, instanceAlias, region, results } where results is
 * [{ resourceType, resourceName, id, arn? }]. Guarded by expectedVersion (409 reason on conflict).
 */
export async function recordImportResults(id, expectedVersion, { instanceId, instanceAlias, region, results }, tokenProvider) {
  return call('POST', `projects/${encodeURIComponent(id)}/import-results`, {
    body: { expectedVersion, instanceId, instanceAlias, region, results },
    tokenProvider,
  });
}
